'use client';

import { DomesticCard } from '@ahamove/icons/order';
import { Visa } from '@ahamove/icons/payment/card';

import { PaymentMethodCode } from '@ahm/api-wrappers-core';
import { formatCurrency, isServer } from '@ahm/common-helpers';

import { useAuth } from '@ahm/react-auth';
import {
  Avatar,
  Button,
  Card,
  Group,
  rem,
  Stack,
  Text,
  Title,
  LoadingOverlay,
  Alert,
} from '@mantine/core';
import { ChevronRight } from 'lucide-react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import InfoIcon from '@/assets/icons/info-blue-icon.svg';

import type { NewPaymentMethod } from '@/components/modals/payment-modal/components/payment-selection-item';

import {
  usePostBindingWalletMutation,
  usePostSourceCardMutation,
  type BindingWalletRequestParams,
  type BindingWalletRes,
  type PaymentCardRequestParams,
} from '@/features/booking/api/payment';

import { usePaymentMethodHook } from '@/hooks';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { objectToQuery } from '@/lib/utils';
import { useLocaleSuffix } from '@/navigation';

// ============ TYPES ============
interface PaymentItemProps {
  item: NewPaymentMethod;
  leftIcon: ReactNode;
  onClick: () => void;
  localSuffix: string;
  isMobile: boolean;
  disabled?: boolean;
}

// ============ CONSTANTS ============
const CARD_METHOD_IDS = {
  DOMESTIC: '01',
  INTERNATIONAL: '02',
} as const;

const WALLET_REDIRECT_MAP = {
  [PaymentMethodCode.Zalopay.toLowerCase()]: 'binding_qr_link',
  [PaymentMethodCode.Shopeepay.toLowerCase()]: 'redirect_url_web',
  [PaymentMethodCode.Momo.toLowerCase()]: 'qr_code_url',
} as const;

// ============ COMPONENTS ============
const PaymentItem = ({
  item,
  leftIcon,
  onClick,
  localSuffix,
  isMobile,
  disabled = false,
}: PaymentItemProps) => (
  <Button
    component="div"
    fullWidth
    variant="subtle"
    color="neutral"
    py={rem(12)}
    px={isMobile ? rem(16) : rem(24)}
    mih={rem(64)}
    onClick={onClick}
    disabled={disabled}
    radius={0}
    styles={{
      inner: { justifyContent: 'flex-start' },
      label: { width: '100%', flex: '1 1 auto' },
    }}
    leftSection={leftIcon}
    rightSection={<ChevronRight className="mr-1.5 size-5" />}
  >
    <Group
      mih={40}
      styles={{ root: { cursor: disabled ? 'not-allowed' : 'pointer' } }}
    >
      <Stack align="left" gap={rem(0.4)}>
        <Text component="div" fw={700} size="sm" ta="left">
          {item?.[`name_${localSuffix}`]}
        </Text>
      </Stack>
    </Group>
  </Button>
);

// ============ MAIN COMPONENT ============
const AddNewCardModal = () => {
  // ============ HOOKS ============
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const isMobile = useIsMobile();
  const tPayment = useTranslations('payment');
  const localSuffix = useLocaleSuffix();
  const { session } = useAuth();
  const { validPaymentMethodList, needLinkMethod } = usePaymentMethodHook();

  const { mutate: postSourceCardMutation, isPending: isPostingSourceCard } =
    usePostSourceCardMutation();
  const { mutate: bindingEWallet, isPending: isBindingEWallet } =
    usePostBindingWalletMutation();

  // ============ LOCAL STATE ============
  const [isRedirecting, setIsRedirecting] = useState(false);

  const isProcessingRef = useRef(false);

  const isPending = isPostingSourceCard || isBindingEWallet || isRedirecting;

  // ============ COMPUTED VALUES ============
  const user = session?.user;
  const promoCode = searchParams.get('promo_code') ?? undefined;

  const eWalletsList = useMemo(
    () => validPaymentMethodList?.filter((pm) => needLinkMethod(pm)) || [],
    [validPaymentMethodList, needLinkMethod]
  );

  const cardList: NewPaymentMethod[] = useMemo(
    () => [
      {
        name_vi_vn: tPayment('select_method.international_card'),
        name_en_us: tPayment('select_method.international_card'),
        description_vi_vn: tPayment('select_method.international_card_des'),
        description_en_us: tPayment('select_method.international_card_des'),
        method_id: CARD_METHOD_IDS.INTERNATIONAL,
        icon_url: '/static/images/payment/international_card.png',
        code: PaymentMethodCode.Card.toLowerCase(),
      },
      {
        name_vi_vn: tPayment('select_method.domestic_card'),
        name_en_us: tPayment('select_method.domestic_card'),
        description_vi_vn: tPayment('select_method.domestic_card_des'),
        description_en_us: tPayment('select_method.domestic_card_des'),
        method_id: CARD_METHOD_IDS.DOMESTIC,
        icon_url: '@ahamove/icons/order/domestic_card.png',
        code: PaymentMethodCode.Card.toLowerCase(),
      },
    ],
    [tPayment]
  );

  // ============ HELPER FUNCTIONS ============
  const buildReturnUrl = useCallback(
    (methodCode?: string) => {
      const queryParams = {
        paymentAction: 'link',
        promo_code: promoCode,
        ...(methodCode && { methodCode }),
      };

      const localePrefix = localSuffix === 'en_us' ? '/en' : '';
      return `${process.env.NEXT_PUBLIC_APP_URI}${localePrefix}${pathname}?${objectToQuery(queryParams)}`;
    },
    [pathname, localSuffix, promoCode]
  );

  const getWalletRedirectUrl = useCallback(
    (result: BindingWalletRes, methodCode: string): string | null => {
      const redirectKey = WALLET_REDIRECT_MAP[methodCode];
      return redirectKey && redirectKey in result
        ? (result[redirectKey] as string)
        : null;
    },
    []
  );

  // ============ HANDLERS ============
  const handleLinkWallet = useCallback(
    (method: NewPaymentMethod) => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;

      const returnUrl = buildReturnUrl(method.code);

      const params: BindingWalletRequestParams = {
        payment_provider: method.code,
        return_url: returnUrl,
        c_id: user?._id,
      };

      bindingEWallet(params, {
        onSuccess: (result: BindingWalletRes) => {
          window.history.pushState({}, '', returnUrl);

          const redirectUrl = getWalletRedirectUrl(result, method.code);
          if (redirectUrl) {
            setIsRedirecting(true);

            setTimeout(() => {
              router.push(redirectUrl);
            }, 100);
          } else {
            isProcessingRef.current = false;
          }
        },
        onError: (error) => {
          console.error('Wallet binding error:', error);
          isProcessingRef.current = false;
        },
      });
    },
    [bindingEWallet, buildReturnUrl, getWalletRedirectUrl, router, user?._id]
  );

  const handleAddNewCard = useCallback(
    (method: NewPaymentMethod) => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;

      const returnUrl = buildReturnUrl();

      const data: PaymentCardRequestParams = {
        payment_provider: 'epay',
        return_url: returnUrl,
        card_type: method.method_id,
      };

      postSourceCardMutation(data, {
        onSuccess: (res) => {
          if (res?.data?.redirect_url && !isServer) {
            setIsRedirecting(true);

            window.location.href = res.data.redirect_url;
          } else {
            isProcessingRef.current = false;
          }
        },
        onError: (error) => {
          console.error('Card linking error:', error);
          isProcessingRef.current = false;
        },
      });
    },
    [buildReturnUrl, postSourceCardMutation]
  );

  const renderCardIcon = useCallback((methodId: string) => {
    switch (methodId) {
      case CARD_METHOD_IDS.DOMESTIC:
        return <DomesticCard radius={4} height={24} width={24} />;
      case CARD_METHOD_IDS.INTERNATIONAL:
        return <Visa radius={4} height={24} width={24} />;
      default:
        return null;
    }
  }, []);

  return (
    <Stack w="100%" bg="neutral.0" gap={0} pos="relative">
      <LoadingOverlay
        visible={isRedirecting}
        overlayProps={{ radius: 'sm', blur: 2 }}
        loaderProps={{ color: 'primary', type: 'oval' }}
      />

      <Card p={0}>
        {eWalletsList.length > 0 && (
          <>
            <Title order={5} px={rem(16)} py={rem(12)} fw={600} size="sm">
              {tPayment('select_method.e_wallet_title')}
            </Title>
            {eWalletsList.map((item) => (
              <PaymentItem
                key={item.method_id}
                item={item}
                leftIcon={
                  <Avatar
                    radius={4}
                    size={24}
                    src={item.icon_url}
                    alt={item?.[`name_${localSuffix}`]}
                  />
                }
                onClick={() => handleLinkWallet(item)}
                localSuffix={localSuffix}
                isMobile={isMobile}
                disabled={isPending}
              />
            ))}
          </>
        )}

        {cardList.length > 0 && (
          <>
            <Title order={5} px={rem(16)} py={rem(12)} fw={600} size="sm">
              {tPayment('select_method.card_title')}
            </Title>
            {cardList.map((item) => (
              <PaymentItem
                key={item.method_id}
                item={item}
                leftIcon={renderCardIcon(item.method_id)}
                onClick={() => handleAddNewCard(item)}
                localSuffix={localSuffix}
                isMobile={isMobile}
                disabled={isPending}
              />
            ))}
            <Alert
              icon={<InfoIcon />}
              color="secondary.4"
              m={16}
              mt={0}
              p={8}
              styles={{
                icon: { marginRight: 6 },
              }}
            >
              <Text size="sm">
                {tPayment.rich('select_method.add_method_note', {
                  number: formatCurrency(10000),
                  strong: (chunks) => <strong>{chunks}</strong>,
                })}
              </Text>
            </Alert>
          </>
        )}
      </Card>
    </Stack>
  );
};

export default AddNewCardModal;
